import { partial as _partial } from 'lodash';
import React from 'react';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';
import { Button } from './Button';
import { XRounded } from './Icons';
import { buttonTransitions, font, media } from './style-utils';
import { getThemeValue } from './Theme';
import { usePressable } from './usePressable';

const getScopedThemeValue = _partial(getThemeValue, 'RecentItem.Primary');

const LAUNCHER_ITEM_BORDER_RADIUS_SMALL = '10rem';
const LAUNCHER_ITEM_BORDER_RADIUS_LARGE = '10rem';

export const RecentItemClearButton = React.forwardRef((props, ref) => {
  const [{ scale }, set] = useSpring(() => ({
    scale: 1,
    config: {
      tension: 800,
      friction: 35,
    },
  }));

  const handlePressStart = () => set({ scale: 0.94 });
  const handlePressEnd = () => set({ scale: 1 });

  const bind = usePressable(handlePressStart, handlePressEnd);

  return (
    <Wrapper
      className={props.className}
      onClick={props.onClick}
      ref={ref}
      style={{ transform: scale.interpolate((scale) => `scale(${scale})`) }}
    >
      <Container {...bind}>
        <InnerContainer>
          <TextContainer>
            <Text>{props.children}</Text>
            <StyledX />
          </TextContainer>
        </InnerContainer>
      </Container>
    </Wrapper>
  );
});

const StyledX = styled(XRounded)`
  width: 1.2em;
  opacity: 0.5;
  margin-left: 0.2em;
`;

const Text = styled.span``;

const Container = styled(Button)`
  background-color: ${getScopedThemeValue('Default.Background')};
  border-radius: ${LAUNCHER_ITEM_BORDER_RADIUS_SMALL};

  position: relative;
  width: auto;

  padding-bottom: 0;
  padding-top: 0;
  padding-left: 1.2rem;
  padding-right: 0.6rem;

  cursor: pointer;

  ${media.tabletLandscapeAndUp`
    border-radius: ${LAUNCHER_ITEM_BORDER_RADIUS_LARGE};
    padding-left: 2.4rem;
    padding-right: 1.2rem;
  `};

  ${buttonTransitions(['background-color'])}

  ${Text}, ${StyledX} {
    color: ${getScopedThemeValue('Default.Foreground')};
    ${buttonTransitions(['color'])}
  }

  @media (hover: hover) {
    :hover {
      background-color: ${getScopedThemeValue('Hover.Background')};

      ${Text}, ${StyledX} {
        color: ${getScopedThemeValue('Hover.Foreground')};
      }
    }
  }
`;

const InnerContainer = styled.div`
  align-items: center;
  display: inline-flex;
  height: 2.6rem;
  margin-bottom: 0;
  margin-top: 0;

  ${media.tabletLandscapeAndUp`
    height: 3.4rem;
    margin-bottom: 0;
    margin-top: 0;
  `};
`;

const TextContainer = styled.div`
  position: relative;
  width: auto;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${font(12, 'Medium', -0.5)}

  ${media.tabletLandscapeAndUp`
    ${font(16)}
  `};

  & > * {
    flex-shrink: 0;
  }

  ${Text} {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    white-space: nowrap;
  }
`;

const Wrapper = styled(animated.div)`
  transform-style: preserve-3d;
  margin-bottom: 0.8rem;
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.tabletLandscapeAndUp`
    margin-bottom: 1rem;
    margin-top: 1rem;
  `};
`;
